import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { FileText, Download, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResultadoViewer() {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [solicitud, setSolicitud] = useState(null);

  useEffect(() => {
    checkPdfExists();
  }, [id]);

  const checkPdfExists = async () => {
    try {
      // Try to get solicitud info to verify PDF exists
      const response = await axios.get(`${API}/solicitudes/${id}/whatsapp-link`);
      if (response.data.success) {
        setSolicitud({ id });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'No se encontró el resultado');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    window.open(`${API}/solicitudes/${id}/resultado-pdf`, '_blank');
  };

  const handleView = () => {
    window.open(`${API}/solicitudes/${id}/resultado-pdf/view`, '_blank');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#505BB3] mx-auto mb-4" />
          <p className="text-slate-600">Cargando resultado...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Resultado no disponible</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-[#505BB3] text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <img src="/amplicare-logo.svg" alt="Amplicare Lab" className="h-10 brightness-0 invert" />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Card Header */}
          <div className="bg-[#505BB3] px-6 py-4">
            <div className="flex items-center gap-3 text-white">
              <FileText className="w-6 h-6" />
              <h1 className="text-xl font-bold">Resultados de Laboratorio</h1>
            </div>
          </div>

          {/* Card Content */}
          <div className="p-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-green-800 font-medium">
                ✓ Los resultados están disponibles
              </p>
              <p className="text-green-600 text-sm mt-1">
                Puede visualizar o descargar el PDF con los resultados de laboratorio.
              </p>
            </div>

            {/* PDF Viewer Embed */}
            <div className="border border-slate-200 rounded-lg overflow-hidden mb-6">
              <iframe
                src={`${API}/solicitudes/${id}/resultado-pdf/view`}
                className="w-full h-[600px]"
                title="Resultados de Laboratorio"
              />
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleView}
                variant="outline"
                className="flex-1 border-[#505BB3] text-[#505BB3] hover:bg-[#505BB3] hover:text-white"
              >
                <FileText className="w-4 h-4 mr-2" />
                Abrir en nueva pestaña
              </Button>
              <Button
                onClick={handleDownload}
                className="flex-1 bg-[#505BB3] hover:bg-[#404A9C] text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Descargar PDF
              </Button>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-slate-50 px-6 py-4 border-t border-slate-200">
            <p className="text-sm text-slate-500 text-center">
              Amplicare Lab - Laboratorio Veterinario
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
